// StockCardDemoPage — yeni stok kartı tasarımının önizleme / POC sayfası.
// Mevcut StocksPage'i bozmadan, yeni hero strip + tab navigation + split button
// pattern'lerini canlı görmek için bağımsız demo.
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui';

// ── Sahte veri ────────────────────────────────────────────────
const MOCK_STOCK = {
  code: 'STK-001',
  name: 'Vidalı Conta M8 Galvaniz',
  category: 'Demir-Çelik',
  subcategory: 'Bağlantı Elemanları',
  unit: 'Adet',
  is_active: true,
  current_stock: 245,
  warehouse_count: 4,
  min_level: 50,
  monthly_movement: 87,
  last_sale_price: 1250.00,
  last_purchase_price: 890.00,
  currency: '₺',
  barcode: '8690123456789',
  vat_rate: 20,
  description: 'M8 paslanmaz galvaniz kaplama vidalı conta. DIN 933 standardı.',
};

const MOCK_PRICES = [
  { name: 'Toptan Liste',    price: 1100.00, currency: '₺', vat_included: false },
  { name: 'Perakende Liste', price: 1250.00, currency: '₺', vat_included: true  },
  { name: 'Bayi Özel',       price:  980.00, currency: '₺', vat_included: false },
  { name: 'USD Liste',       price:   42.50, currency: '$', vat_included: false },
];

const MOCK_WAREHOUSES = [
  { code: 'Merkez',  name: 'Merkez Depo',   qty: 145, min: 30 },
  { code: 'Şube-1',  name: 'Bursa Şubesi',  qty:  62, min: 10 },
  { code: 'Şube-2',  name: 'Ankara Şubesi', qty:  38, min: 10 },
  { code: 'Arşiv',   name: 'İade Stok',     qty:   0, min:  0 },
];

const MOCK_MOVEMENTS = [
  { date: '2026-05-13 14:22', type: 'Satış',   doc: 'SF20260000000124', qty: -12, balance: 245, who: 'Cari A.Ş.' },
  { date: '2026-05-13 11:08', type: 'Alış',    doc: 'AF20260000000045', qty: +50, balance: 257, who: 'Tedarikçi Ltd.' },
  { date: '2026-05-12 16:55', type: 'Satış',   doc: 'SF20260000000122', qty:  -8, balance: 207, who: 'Müşteri B' },
  { date: '2026-05-11 09:30', type: 'Transfer',doc: 'TF20260000000018', qty: -20, balance: 215, who: 'Merkez → Bursa' },
  { date: '2026-05-10 13:14', type: 'İade',    doc: 'SF20260000000115', qty:  +3, balance: 235, who: 'Cari A.Ş.' },
];

const TABS = [
  { id: 'general',   label: 'Genel',     icon: '📋' },
  { id: 'prices',    label: 'Fiyatlar',  icon: '💰' },
  { id: 'stocks',    label: 'Stoklar',   icon: '🏬' },
  { id: 'movements', label: 'Hareketler',icon: '🔄', badge: 87 },
  { id: 'lots',      label: 'Lot / Seri',icon: '📦' },
  { id: 'docs',      label: 'Belgeler',  icon: '📎' },
  { id: 'stats',     label: 'İstatistik',icon: '📊' },
];

// ── Bileşenler ────────────────────────────────────────────────

function KpiBadge({ label, value, color = 'primary', sublabel }) {
  const COLORS = {
    primary: { fg: 'var(--accent-primary)', bg: 'var(--accent-glow)' },
    success: { fg: 'var(--success)', bg: 'rgba(22,163,74,0.10)' },
    warning: { fg: 'var(--warning)', bg: 'rgba(245,158,11,0.10)' },
    neutral: { fg: 'var(--text-secondary)', bg: 'var(--bg-secondary)' },
  };
  const c = COLORS[color];
  return (
    <div style={{
      background: c.bg,
      borderRadius: 'var(--r-lg)',
      padding: '10px 14px',
      minWidth: 110,
    }}>
      <div style={{ fontSize: '1.35rem', fontWeight: 800, fontFamily: 'monospace', color: c.fg, lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2 }}>{label}</div>
      {sublabel && <div style={{ fontSize: '0.66rem', color: 'var(--text-tertiary)' }}>{sublabel}</div>}
    </div>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-secondary)',
      borderRadius: 'var(--r-lg)',
      padding: '14px 16px',
      marginBottom: 12,
    }}>
      <div style={{
        fontSize: '0.78rem',
        fontWeight: 700,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 10,
        paddingBottom: 6,
        borderBottom: '1px solid var(--border-secondary)',
      }}>{title}</div>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, hint, required, type = 'text' }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: '0.75rem',
        color: 'var(--text-secondary)', marginBottom: 4,
        fontWeight: 600,
      }}>
        {label} {required && <span style={{ color: 'var(--danger)' }}>*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea defaultValue={value} rows={3} style={{
          width: '100%', padding: '8px 10px',
          border: '1px solid var(--border-secondary)',
          borderRadius: 'var(--r-md)', fontSize: '0.85rem',
          background: 'var(--bg-primary)', color: 'var(--text-primary)',
          fontFamily: 'inherit', resize: 'vertical',
        }} />
      ) : (
        <input type={type} defaultValue={value} style={{
          width: '100%', padding: '8px 10px',
          border: '1px solid var(--border-secondary)',
          borderRadius: 'var(--r-md)', fontSize: '0.85rem',
          background: 'var(--bg-primary)', color: 'var(--text-primary)',
          fontFamily: type === 'number' ? 'monospace' : 'inherit',
        }} />
      )}
      {hint && <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function SplitButton({ primaryLabel, primaryAction, options = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button onClick={primaryAction} style={{
        background: 'var(--accent-primary)',
        color: '#fff', border: 'none',
        padding: '8px 16px', fontSize: '0.85rem', fontWeight: 600,
        borderRadius: 'var(--r-md) 0 0 var(--r-md)',
        cursor: 'pointer',
      }}>{primaryLabel}</button>
      <button onClick={() => setOpen(o => !o)} style={{
        background: 'var(--accent-primary)',
        color: '#fff', border: 'none',
        borderLeft: '1px solid rgba(255,255,255,0.2)',
        padding: '8px 10px', cursor: 'pointer',
        borderRadius: '0 var(--r-md) var(--r-md) 0',
      }}>▼</button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: 'var(--bg-card)',
          border: '1px solid var(--border-secondary)',
          borderRadius: 'var(--r-md)',
          minWidth: 200, zIndex: 100,
          boxShadow: 'var(--shadow-lg)',
          padding: 4,
        }}>
          {options.map(opt => (
            <button key={opt.label} onClick={() => { opt.onClick?.(); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 10px', background: 'transparent', border: 'none',
                cursor: 'pointer', fontSize: '0.85rem',
                color: 'var(--text-primary)',
                borderRadius: 'var(--r-sm)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              {opt.icon && <span style={{ marginRight: 8 }}>{opt.icon}</span>}{opt.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// ── Tab içerikleri ────────────────────────────────────────────

function GeneralTab() {
  return (
    <>
      <FieldGroup title="Tanımlayıcı">
        <Field label="Stok Kodu" value={MOCK_STOCK.code} required hint="Benzersiz olmalı" />
        <Field label="Stok Adı" value={MOCK_STOCK.name} required />
        <Field label="Barkod" value={MOCK_STOCK.barcode} hint="EAN-13 / Code-128" />
        <Field label="Birim" value={MOCK_STOCK.unit} />
      </FieldGroup>

      <FieldGroup title="Kategori & Vergi">
        <Field label="Kategori" value={MOCK_STOCK.category} />
        <Field label="Alt Kategori" value={MOCK_STOCK.subcategory} />
        <Field label="KDV Oranı (%)" value={MOCK_STOCK.vat_rate} type="number" />
        <Field label="GTİP" value="" hint="Gümrük Tarife İstatistik Pozisyonu" />
      </FieldGroup>

      <FieldGroup title="Açıklama">
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Notlar" value={MOCK_STOCK.description} type="textarea" />
        </div>
      </FieldGroup>
    </>
  );
}

function PricesTab() {
  return (
    <FieldGroup title="Fiyat Listeleri">
      <div style={{ gridColumn: '1 / -1' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)' }}>
              <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)' }}>Liste Adı</th>
              <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--border-secondary)' }}>Fiyat</th>
              <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--border-secondary)' }}>Birim</th>
              <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--border-secondary)' }}>KDV Dahil</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_PRICES.map(p => (
              <tr key={p.name}>
                <td style={{ padding: '8px', borderBottom: '1px solid var(--border-secondary)' }}>{p.name}</td>
                <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--border-secondary)', fontFamily: 'monospace', fontWeight: 600 }}>
                  {p.price.toFixed(2)}
                </td>
                <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--border-secondary)' }}>{p.currency}</td>
                <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--border-secondary)' }}>
                  {p.vat_included ? '✓' : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button size="sm" variant="ghost" style={{ marginTop: 10 }}>+ Liste Ekle</Button>
      </div>
    </FieldGroup>
  );
}

function StocksTab() {
  return (
    <FieldGroup title="Depo Bakiyeleri">
      <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {MOCK_WAREHOUSES.map(w => {
          const critical = w.qty < w.min;
          const empty = w.qty === 0;
          return (
            <div key={w.code} style={{
              padding: 12,
              background: 'var(--bg-primary)',
              border: `1px solid ${empty ? 'var(--border-secondary)' : critical ? 'var(--warning)' : 'var(--border-secondary)'}`,
              borderRadius: 'var(--r-md)',
              borderLeftWidth: 3,
              borderLeftColor: empty ? 'var(--text-tertiary)' : critical ? 'var(--warning)' : 'var(--success)',
            }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{w.code}</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, fontFamily: 'monospace', color: empty ? 'var(--text-tertiary)' : critical ? 'var(--warning)' : 'var(--accent-primary)' }}>
                {w.qty}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>
                {w.name} · min {w.min}
              </div>
            </div>
          );
        })}
      </div>
    </FieldGroup>
  );
}

function MovementsTab() {
  return (
    <FieldGroup title="Son Hareketler">
      <div style={{ gridColumn: '1 / -1' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-secondary)' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)' }}>Tarih</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)' }}>Tip</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)' }}>Belge</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)' }}>Cari</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid var(--border-secondary)' }}>Miktar</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid var(--border-secondary)' }}>Kalan</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_MOVEMENTS.map((m, i) => (
              <tr key={i}>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-secondary)' }}>{m.date}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-secondary)' }}>
                  <span style={{
                    fontSize: '0.7rem', padding: '2px 6px',
                    borderRadius: 'var(--r-full)', fontWeight: 600,
                    background: m.qty > 0 ? 'rgba(22,163,74,0.10)' : 'rgba(220,38,38,0.10)',
                    color: m.qty > 0 ? 'var(--success)' : 'var(--danger)',
                  }}>{m.type}</span>
                </td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-secondary)', fontFamily: 'monospace', fontSize: '0.78rem' }}>{m.doc}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-secondary)' }}>{m.who}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-secondary)', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: m.qty > 0 ? 'var(--success)' : 'var(--danger)' }}>
                  {m.qty > 0 ? '+' : ''}{m.qty}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border-secondary)', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{m.balance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </FieldGroup>
  );
}

function EmptyTab({ title, hint }) {
  return (
    <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📋</div>
      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{title}</div>
      <div style={{ fontSize: '0.8rem', marginTop: 6 }}>{hint}</div>
    </div>
  );
}

// ── Ana sayfa ─────────────────────────────────────────────────

export default function StockCardDemoPage() {
  const [activeTab, setActiveTab] = useState('general');
  const [drawerMode, setDrawerMode] = useState(false);

  const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  const renderTab = () => {
    switch (activeTab) {
      case 'general':   return <GeneralTab />;
      case 'prices':    return <PricesTab />;
      case 'stocks':    return <StocksTab />;
      case 'movements': return <MovementsTab />;
      case 'lots':      return <EmptyTab title="Lot / Seri / Parti" hint="Bu üründe lot takibi açıkken burası dolacak" />;
      case 'docs':      return <EmptyTab title="Belgeler / Fotoğraflar" hint="Henüz belge eklenmemiş" />;
      case 'stats':     return <EmptyTab title="İstatistik" hint="Aylık satış grafiği, top müşteri, abc analizi" />;
      default: return null;
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: drawerMode ? 720 : 1200, margin: '0 auto' }}>
      {/* Demo başlığı */}
      <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(99,102,241,0.08)', borderRadius: 'var(--r-md)', borderLeft: '3px solid #6366f1' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#6366f1' }}>📦 DEMO — Önerilen Yeni Stok Kartı Tasarımı</div>
        <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: 2 }}>
          Hero strip · Tab navigation · Split button · FieldGroup · KPI rozetleri — sahte veri ile çalışıyor
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={drawerMode} onChange={(e) => setDrawerMode(e.target.checked)} />
            <span>Drawer modu (720px sağ panel hissi)</span>
          </label>
        </div>
      </div>

      {/* ─── HERO STRIP ─────────────────────────────────── */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-secondary)',
        borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
        padding: '14px 18px',
        borderBottom: 'none',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: '1.4rem' }}>📦</span>
              <div>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{MOCK_STOCK.code}</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)' }}>{MOCK_STOCK.name}</div>
              </div>
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginLeft: 32 }}>
              {MOCK_STOCK.category} → {MOCK_STOCK.subcategory}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{
              fontSize: '0.72rem', padding: '4px 10px',
              borderRadius: 'var(--r-full)', fontWeight: 700,
              background: 'rgba(22,163,74,0.12)', color: 'var(--success)',
            }}>● Aktif</span>
            <button title="Favorilere ekle" style={{
              background: 'transparent', border: '1px solid var(--border-secondary)',
              padding: '6px 10px', borderRadius: 'var(--r-md)',
              cursor: 'pointer', fontSize: '0.85rem',
            }}>☆</button>
            <button title="Kopya oluştur" style={{
              background: 'transparent', border: '1px solid var(--border-secondary)',
              padding: '6px 10px', borderRadius: 'var(--r-md)',
              cursor: 'pointer', fontSize: '0.85rem',
            }}>⎘</button>
          </div>
        </div>

        {/* KPI rozetleri */}
        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <KpiBadge label="Toplam Stok" value={MOCK_STOCK.current_stock} color="success" sublabel={`${MOCK_STOCK.warehouse_count} depoda`} />
          <KpiBadge label="Son Satış" value={fmt(MOCK_STOCK.last_sale_price)} color="primary" sublabel={`${MOCK_STOCK.currency} · KDV dahil`} />
          <KpiBadge label="Son Alış"  value={fmt(MOCK_STOCK.last_purchase_price)} color="neutral" sublabel={`${MOCK_STOCK.currency} · KDV hariç`} />
          <KpiBadge label="Aylık Hrk." value={MOCK_STOCK.monthly_movement} color="warning" sublabel="son 30 gün" />
          <KpiBadge label="Min. Seviye" value={MOCK_STOCK.min_level} color="neutral" sublabel="kritik eşik" />
        </div>
      </div>

      {/* ─── TAB BAR ───────────────────────────────────── */}
      <div style={{
        background: 'var(--bg-secondary)',
        borderLeft: '1px solid var(--border-secondary)',
        borderRight: '1px solid var(--border-secondary)',
        display: 'flex', gap: 0,
        overflowX: 'auto',
      }}>
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 14px',
                background: active ? 'var(--bg-card)' : 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid var(--accent-primary)' : '2px solid transparent',
                color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
                fontSize: '0.82rem',
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                transition: 'all 0.15s',
              }}>
              <span style={{ fontSize: '0.95rem' }}>{tab.icon}</span>
              {tab.label}
              {tab.badge && (
                <span style={{
                  fontSize: '0.65rem', padding: '1px 6px',
                  borderRadius: 'var(--r-full)',
                  background: active ? 'var(--accent-primary)' : 'var(--text-tertiary)',
                  color: '#fff', fontWeight: 700, marginLeft: 2,
                }}>{tab.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ─── TAB İÇERİĞİ ────────────────────────────────── */}
      <div style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-secondary)',
        borderTop: 'none',
        padding: 16,
        minHeight: 400,
      }}>
        {renderTab()}
      </div>

      {/* ─── FOOTER (action bar) ────────────────────────── */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-secondary)',
        borderTop: 'none',
        borderRadius: '0 0 var(--r-lg) var(--r-lg)',
        padding: '12px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <div>
          <Button variant="ghost" size="sm" style={{ color: 'var(--danger)' }}>🗑 Sil</Button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
            ⓘ Değişiklikler kaydedilmedi
          </span>
          <Button variant="ghost" size="sm">İptal</Button>
          <SplitButton
            primaryLabel="💾 Kaydet"
            primaryAction={() => alert('Kaydet → demo')}
            options={[
              { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => alert('Yeni aç → demo') },
              { label: 'Kaydet ve Kopyala',  icon: '⎘', onClick: () => alert('Kopyala → demo') },
              { label: 'Kaydet ve Yazdır',   icon: '🖨', onClick: () => alert('Yazdır → demo') },
              { label: 'Kaydet ve Kapat',    icon: '✓', onClick: () => alert('Kapat → demo') },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
